import {useState, useEffect} from 'react';
import useYupValidationResolver from "../../util/useYupValidationResolver";
import {FormProvider, useForm} from 'react-hook-form';
import Field from "../form/Field";
import settingsSchema from "./validation/settingsSchema";
import {showErrorNotification, showSuccessNotification} from "../../util/notifications";
import useApi from "../../api";
import {useQuery, useQueryClient} from "react-query";
import queryConfig from "../../util/queryConfig";
import classNames from 'classnames';
import Throbber from '../throbber/Throbber';
import { QueryKey } from '../../constants/queryKey';

const GoogleSettings = () => {
    const {getGoogleSettings, editGoogleSettings} = useApi();
    const [loading, setLoading] = useState(false);
    const [google, setGoogle] = useState('GA');
    const queryClient = useQueryClient();
    const {isLoading, data} = useQuery([QueryKey.GoogleSettings], async () => {
        const response = await getGoogleSettings();
        return response.data;
    }, queryConfig);
    const resolver = useYupValidationResolver(settingsSchema);
    const formMethods = useForm({
        resolver,
    });

    useEffect(() => {
        if (!data || !data.google) {
            return;
        }
        setGoogle(data.google.ACTIVE);
    }, [data])

    const {handleSubmit} = formMethods;

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const response = await editGoogleSettings({
                ...data,
                ACTIVE: google,
            });
            await queryClient.invalidateQueries('googleSettings');
            if (response.data && response.data.success) {
                showSuccessNotification('Google settings saved.');
            } else {
                showErrorNotification('Something went wrong saving Google settings.');
            }
        } catch (e) {
            console.error(e);
            showErrorNotification('Something went wrong saving Google settings.');
        }
        setLoading(false);
    }

    const onError = (error) => {
        console.log(error);
        showErrorNotification('Settings not saved. Please check the fields.');
    }

    const buttonClass = classNames('button', {
        'button--loading': loading,
    });

    const onGoogleRadioClick = (e) => {
        console.log(e.target.value);
        setGoogle(e.target.value);
    }

    if (isLoading) {
        return <Throbber title="Loading settings"/>;
    }

    return (
        <FormProvider {...formMethods} >
            <form onSubmit={handleSubmit(onSubmit, onError)} noValidate className="form">
                <div className="form__group">
                    <input
                        type="radio"
                        className="form__field form__field--checkbox"
                        id="google_none"
                        name="google"
                        value="NONE"
                        checked={google === 'NONE'}
                        onChange={onGoogleRadioClick}
                    />
                    <label className="form__label form__label--radio" htmlFor="google_none">
                        Do not use Google Analytics or Tag Manager
                    </label>
                </div>
                <div className="form__group">
                    <input
                        type="radio"
                        className="form__field form__field--checkbox"
                        id="google_ga"
                        name="google"
                        value="GA"
                        checked={google === 'GA'}
                        onChange={onGoogleRadioClick}
                    />
                    <label className="form__label form__label--radio" htmlFor="google_ga">
                        Use Google Analytics
                    </label>
                </div>
                <div className={`form__sub-group ${google !== 'GA' ? 'hidden' : ''}`}>
                    <Field
                        type="text"
                        name="GA"
                        maxLength={50}
                        placeholder="UA-XXXXX-Y"
                        defaultValue={data?.google?.GA || ''}
                    />
                </div>
                <div className="form__group">
                    <input
                        type="radio"
                        className="form__field form__field--checkbox"
                        id="google_gtm"
                        name="google"
                        value="GTM"
                        checked={google === 'GTM'}
                        onChange={onGoogleRadioClick}
                    />
                    <label className="form__label form__label--radio" htmlFor="google_gtm">
                        Use Google Tag Manager
                    </label>
                </div>
                <div className={`form__sub-group ${google !== 'GTM' ? 'hidden' : ''}`}>
                    <Field
                        type="text"
                        name="GTM"
                        maxLength={50}
                        placeholder="GTM-XXXXXXX"
                        defaultValue={data?.google?.GTM || ''}
                    />
                </div>
                <button className={buttonClass}>Save</button>
            </form>
        </FormProvider>
    )
};

export default GoogleSettings;
